import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Check, X } from 'lucide-react';
import { Drawer, DrawerContentRight, DrawerTitle } from '../ui/drawer';
import {
  ACCENT_PRESETS,
  DEFAULT_APPEARANCE,
  FONT_FAMILY_OPTIONS,
  getEffectiveAccentHex,
  getFontFamilyStack,
  type AppearanceSettings,
  type FontFamilyKey,
} from '../../lib/appearance';
import { useAppearanceStore } from '../../store/appearanceStore';
import { useTheme } from '../../store/themeStore';
import { showToast } from '../../store/toastStore';
import { SettingsOptionTile, SettingsSection } from './SettingsOptionTile';
import { SidebarFullIcon, SidebarMiniIcon } from './settingsLayoutIcons';
import '../../styles/appearance-settings.css';

interface AppearanceSettingsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function cloneSettings(settings: AppearanceSettings): AppearanceSettings {
  return { ...settings };
}

export function AppearanceSettingsDrawer({ open, onOpenChange }: AppearanceSettingsDrawerProps) {
  const { theme } = useTheme();
  const appliedSettings = useAppearanceStore((s) => s.settings);
  const applySettings = useAppearanceStore((s) => s.applySettings);
  const resetSettings = useAppearanceStore((s) => s.resetSettings);

  const [draft, setDraft] = useState<AppearanceSettings>(() => cloneSettings(appliedSettings));
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(cloneSettings(appliedSettings));
    }
    wasOpenRef.current = open;
  }, [open, appliedSettings]);

  const patchDraft = useCallback((partial: Partial<AppearanceSettings>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const customAccentValue = useMemo(() => draft.accentHex ?? '#5B7FFF', [draft.accentHex]);

  const effectiveAccent = useMemo(
    () => getEffectiveAccentHex(draft.accentHex, theme),
    [draft.accentHex, theme]
  );

  const drawerAccentStyle = useMemo(
    () => ({ '--settings-accent': effectiveAccent }) as CSSProperties,
    [effectiveAccent]
  );

  const activePresetId = useMemo(() => {
    if (!draft.accentHex) return null;
    return (
      ACCENT_PRESETS.find((p) => p.hex.toLowerCase() === draft.accentHex?.toLowerCase())?.id ?? null
    );
  }, [draft.accentHex]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && open) {
      setDraft(cloneSettings(appliedSettings));
    }
    onOpenChange(nextOpen);
  };

  const handleDone = () => {
    applySettings(cloneSettings(draft));
    showToast('Changes applied', 'success', {
      description: 'Your appearance settings are now active.',
    });
    onOpenChange(false);
  };

  const handleReset = () => {
    const defaults = cloneSettings(DEFAULT_APPEARANCE);
    setDraft(defaults);
    resetSettings();
    showToast('Settings reset', 'success', {
      description: 'Defaults restored. You can customize again or close with Done.',
    });
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} direction="right">
      <DrawerContentRight className="appearance-settings-drawer" style={drawerAccentStyle}>
        <div className="appearance-settings-drawer__header">
          <DrawerTitle className="appearance-settings-drawer__heading">Settings</DrawerTitle>
          <button
            type="button"
            className="appearance-settings-drawer__close"
            aria-label="Close settings"
            onClick={() => handleOpenChange(false)}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="appearance-settings-drawer__body">
          <SettingsSection title="Sidebar Type">
            <div className="settings-option-grid settings-option-grid--2">
              <SettingsOptionTile
                label="Full"
                selected={draft.sidebarType === 'full'}
                onClick={() => patchDraft({ sidebarType: 'full' })}
              >
                <SidebarFullIcon active={draft.sidebarType === 'full'} accentColor={effectiveAccent} />
              </SettingsOptionTile>
              <SettingsOptionTile
                label="Mini"
                selected={draft.sidebarType === 'mini'}
                onClick={() => patchDraft({ sidebarType: 'mini' })}
              >
                <SidebarMiniIcon active={draft.sidebarType === 'mini'} accentColor={effectiveAccent} />
              </SettingsOptionTile>
            </div>
          </SettingsSection>

          <SettingsSection title="Font Family">
            <div className="settings-option-grid settings-option-grid--2">
              {FONT_FAMILY_OPTIONS.map((font) => {
                const selected = draft.fontFamily === font.value;
                const stack = getFontFamilyStack(font.value);
                return (
                  <SettingsOptionTile
                    key={font.value}
                    label={font.cardLabel}
                    selected={selected}
                    onClick={() => patchDraft({ fontFamily: font.value as FontFamilyKey })}
                    className={font.value === 'system' ? 'settings-option-tile--span-2' : undefined}
                  >
                    <span className="settings-font-preview" style={{ fontFamily: stack }}>
                      <span className="settings-font-preview__sample">{font.sample}</span>
                      <span className="settings-font-preview__line">Dashboard</span>
                    </span>
                  </SettingsOptionTile>
                );
              })}
            </div>
          </SettingsSection>

          <section className="settings-section">
            <h3 className="settings-section__title">Accent color</h3>
            <p className="settings-section__hint">Applies across buttons, links, and charts</p>
            <div className="settings-color-grid settings-color-grid--accent">
              {ACCENT_PRESETS.map((preset) => {
                const selected = activePresetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={`settings-color-tile${selected ? ' is-selected' : ''}`}
                    aria-label={`${preset.label} accent`}
                    aria-pressed={selected}
                    onClick={() => patchDraft({ accentHex: preset.hex })}
                  >
                    {selected ? (
                      <span className="settings-color-tile__badge" aria-hidden>
                        <Check size={10} strokeWidth={3} />
                      </span>
                    ) : null}
                    <span
                      className="settings-color-tile__swatch"
                      style={{ backgroundColor: preset.hex }}
                    />
                    <span className="settings-color-tile__label">{preset.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
          <section className="settings-section settings-section--compact">
            <div className="appearance-custom-accent">
              <label htmlFor="appearance-accent-picker" className="appearance-custom-accent__label">
                Custom
              </label>
              <input
                id="appearance-accent-picker"
                type="color"
                className="appearance-custom-accent__input"
                value={customAccentValue}
                onChange={(e) => patchDraft({ accentHex: e.target.value })}
                aria-label="Pick custom accent color"
              />
              <button
                type="button"
                className="appearance-custom-accent__reset"
                onClick={() => patchDraft({ accentHex: null })}
              >
                Theme default
              </button>
            </div>
          </section>
        </div>

        <div className="appearance-settings-drawer__footer">
          <button type="button" className="settings-footer-btn settings-footer-btn--outline" onClick={handleReset}>
            Reset
          </button>
          <button type="button" className="settings-footer-btn settings-footer-btn--primary" onClick={handleDone}>
            Done
          </button>
        </div>
      </DrawerContentRight>
    </Drawer>
  );
}
